import { Injectable } from "@angular/core";
import { AppConfig } from "../shared/constants/app.config";
import {BehaviorSubject, forkJoin, from, Observable} from 'rxjs';
import { HttpClient } from "@angular/common/http";
import { UtilityService } from "../shared/service/utility.service";
import {catchError, filter, map, mergeMap, retry} from 'rxjs/operators';

import * as uuid from 'uuid';
import {AppStateService} from "../shared/service/app.state.service";

@Injectable()
export class ObservationLabsService {
  constructor(
    readonly http: HttpClient,
    readonly utilityService: UtilityService,
    readonly appStateService: AppStateService,
  ) {
    this.appStateService.patient$
    .pipe(
      filter((p): p is string => !!p)
    )
    .subscribe(patient => {
      this.patientId = patient;
    });
  }

  patientId: string;

  priorityLabs$ = new BehaviorSubject<boolean>(false);

  setPriorityLabsValue(isPriority) {
    this.priorityLabs$.next(isPriority);
  }

  getPriorityLabsValue() {
    return this.priorityLabs$.value;
  }

  // Below method submit new records to the cibmtr
  getBundles(selectedResources, psScope): Array<Observable<any>> {
    const selectedChunkResources = this.utilityService.chunk(
      selectedResources,
      this.utilityService.chunkSize
    );
    const bundles = [];

    selectedChunkResources.forEach((selectedChunkResource) => {
      bundles.push(this.getBundleEntry(selectedChunkResource, psScope));
    });
    return bundles;
  }

  getBundleObservable(bundle) {
    return this.http.post(localStorage.getItem("cibmtr_url") + "Bundle", bundle).pipe(
      map(() => bundle),
      retry(1),
      catchError((error) => {
        throw error;
      })
    );
  }

  // Below method submit updated records to the cibmtr
  getBundleEntry(selectedNewChunkResource, psScope) {
    const entries = [];
    selectedNewChunkResource.forEach((selectedChunkElement) => {
      if (selectedChunkElement.state === "normal") {
        const { fullUrl, resource } = selectedChunkElement;
        entries.push({
          fullUrl: "urn:uuid:" + uuid.v4(),
          resource: {
            ...resource,
            meta: {
              security: [
                {
                  system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
                  code: psScope,
                },
              ],
            },
            identifier: [
              {
                use: "official",
                system: AppConfig.FHIR_LOGICALID_NAMESPACE,
                value: fullUrl,
              },
            ]
          },
          request: {
            method: "PUT",
            url: `Observation/${resource.id}`,
          },
        });
      } else {
        const { fullUrl, resource } = selectedChunkElement;
        delete resource.id;
        entries.push({
          fullUrl: "urn:uuid:" + uuid.v4(),
          resource: {
            ...resource,
            status: "unknown",
            meta: {
              security: [
                {
                  system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
                  code: psScope,
                },
              ],
            },
            identifier: [
              {
                use: "official",
                system: AppConfig.FHIR_LOGICALID_NAMESPACE,
                value: fullUrl,
              },
            ]
          },
          request: {
            method: "POST",
            url: "Observation",
          },
        });
      }
    });
    return {
      resourceType: "Bundle",
      type: "transaction",
      entry: entries,
    };
  }

  /**
   * Generalized getCibmtrObsLabs method
   * @param subject (iss url)
   * @param psScope (rc-code of center)
   * @param fhirUrl (r4 or r3 cibmtr url)
   * @param isPriorityLabs (is this a prioritylab query or not)
   */
  getCibmtrObservationsLabs(subject, psScope, fhirUrl): Observable<any> {
    const isR4 = /\/r4(\/|$)/i.test(fhirUrl.toString());
    const baseURL = isR4 ? this.buildCibmtrR4Url(psScope, fhirUrl) : this.buildCibmtrR3Url(subject, psScope, fhirUrl);

    return this.getCibmtrLabs(baseURL);
  }

  /**
   * Build cibmtr r4 url using patient local-id
   * @param psScope (rc-code of center)
   * @param fhirUrl (r4 cibmtr url)
   */
  buildCibmtrR4Url(psScope, fhirUrl)
  {
    const observationUrl = 'Observation?subject=Patient/' + localStorage.getItem('patient_local_id');
    return fhirUrl + observationUrl + this.buildQuery(psScope);
  }

  /**
   * build cibmtr r3 url using full patient identifier
   * @param subject (actually the iss url - consider renaming in future)
   * @param psScope (rc-code of center)
   * @param fhirUrl (r3 cibmtr url)
   */
  buildCibmtrR3Url(subject, psScope, fhirUrl)
  {
    // Do not replace  r4 with R3 if found anywhere other than the /r4/ path.
    if (/\/r4(\/|$)/i.test(subject)) {
      subject = subject.replace(/\/r4(\/|$)/i, '/R3$1');
    }

    const observationUrl = 'Observation?subject=' + subject;
    const patientId = '/Patient/' + this.patientId;

    return fhirUrl + observationUrl + patientId + this.buildQuery(psScope);
  }

  /**
   * Construct the query
   * @param psScope (this is actually the rc-code (should rename in future)
   */
  buildQuery(psScope){
    const greaterThan = '&date=ge' + localStorage.getItem('startDate');
    const lesserThan = '&date=le' + localStorage.getItem('endDate');
    const security = '&_security=' + psScope;
    const countQuery = '&_count=500&category=laboratory';

    return security + countQuery + greaterThan + lesserThan;
  }

  /**
   * Call the api for all labs or priorityLabs
   * @param baseUrl - constructed baseURL for R4 or R3
   * @param isPriorityLabs - boolean to include priority-labs loinc codes
   */
  getCibmtrLabs(baseUrl): Observable<any> {
    const isPriorityLabs = this.getPriorityLabsValue();
    if (!isPriorityLabs) {
      return this.http.get(baseUrl);
    }
    const loincChunks =  this.utilityService.chunk(
      AppConfig.LAB_LOINC_CODES.toString().split(','),
      150
    );

    const requests = loincChunks.map((loincCodes) => {
      const codes = '&code=' + loincCodes.join(',');
      return this.http.get(baseUrl + codes);
    });

    // Making sure single bundles get returned to component
    return forkJoin(requests).pipe(
      mergeMap(responses => from(responses))
    );
  }

  submitMsgHeader(msgHeaderBundle: any) {
    const response = this.http.post(localStorage.getItem("cibmtr_url") + "$process-message", msgHeaderBundle).pipe(
      map(() => msgHeaderBundle),
      retry(1),
      catchError((error) => {
        throw error;
      })
    );
    console.log(response);
    return response;
  }

  getMsgHeaderBundle(): any {
    const entries = [];
    entries.push({
      resource: {
        resourceType: "MessageHeader",
        event: {
          system: "http://cibmtr.org/codesystem/reporting-message",
          code: "batch-complete",
          display: "batch complete"
        },
        sender: {
          identifier: [
            {
              system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
              value: this.utilityService.data.selectedOrg.value,
            },
          ],
        },
        "focus": [
          {
            identifier: [
              {
                use: "official",
                system: AppConfig.CIBMTR_CRID_NAMESPACE,
                value: this.utilityService.data.crid,
              },
            ],
            "display": "Patient with CRID " + this.utilityService.data.crid,
          }
        ],
      },
      request: {
        method: "POST",
        url: "MessageHeader",
      },
    });
    return {
      resourceType: "Bundle",
      type: "message",
      entry: entries,
    };
  }
}
